import React, { useEffect, useState } from "react";
import { showSuccess, showError } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import { useParams } from "react-router-dom";
import EMAIL_PROVIDERS from "../../../constants/emailConstants";
import emailAPI from "../../../api/notification/emailApi";

function EmailSettings() {
  const { showLoader, hideLoader } = useLoader();
  const [errors, setErrors] = useState({});
  const [loadingProvider, setLoadingProvider] = useState(null);

  const [emailGateways, setEmailGateways] = useState({
    smtp: {
      enabled: false,
      isDefault: false,
      config: {
        host: "",
        port: "",
        username: "",
        password: "",
        encryption: "",
        fromEmail: "",
        fromName: "",
      },
    },
    mailchimp: {
      enabled: false,
      isDefault: false,
      config: {
        apiKey: "",
        audienceId: "",
        fromEmail: "",
        fromName: "",
      },
    },
    resend: {
      enabled: false,
      isDefault: false,
      config: {
        apiKey: "",
        fromEmail: "",
        fromName: "",
      },
    },
    sendgrid: {
      enabled: false,
      isDefault: false,
      config: {
        apiKey: "",
        fromEmail: "",
        fromName: "",
      },
    },
  });

  const handleToggle = (gatewayName) => {
    setEmailGateways((prev) => ({
      ...prev,
      [gatewayName]: {
        ...prev[gatewayName],
        enabled: !prev[gatewayName].enabled,
      },
    }));
  };

  const handleFieldChange = (provider, field, value) => {
    setEmailGateways((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        config: {
          ...prev[provider].config,
          [field]: value,
        },
      },
    }));
  };

  useEffect(() => {
    fetchGatewayDetails();
  }, []);

  const fetchGatewayDetails = async () => {
    try {
      const res = await emailAPI.emailGatewayDetails();

      if (res.code === 200) {
        const formatted = {};

        const providers = res.data?.providers || [];

        providers.forEach((g) => {
          formatted[g.providerName] = {
            _id: g._id,
            enabled: g.enabled,
            isDefault: g.isDefault || false,
            priority: g.priority || 1,
            config: {
              ...emailGateways[g.providerName]?.config,
              ...g.config,
              ...g.fromIdentity,
              encryption: g.secure ? "ssl" : "tls",
            },
          };
        });

        setEmailGateways((prev) => ({
          ...prev,
          ...formatted,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const validateProvider = (providerName) => {
    const newErrors = {};
    const meta = EMAIL_PROVIDERS[providerName];
    const g = emailGateways[providerName];

    if (!g?.enabled) return true;

    meta.fields.forEach((field) => {
      if (field.required && !g.config[field.name]) {
        newErrors[`${providerName}_${field.name}`] =
          `${field.label} is required`;
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (providerName, g) => {
    return {
      providerName,
      displayName: providerName.toUpperCase(),
      priority: g.priority || 1,
      enabled: g.enabled,
      isDefault: g.isDefault,

      config:
        providerName === "smtp"
          ? {
              host: g.config.host,
              port: Number(g.config.port),
              secure: g.config.encryption === "ssl",
              username: g.config.username,
              password: g.config.password,
            }
          : {
              apiKey: g.config.apiKey,
            },

      fromIdentity: {
        fromEmail: g.config.fromEmail,
        fromName: g.config.fromName,
        replyTo: g.config.replyTo,
      },
    };
  };

  const handleProviderSubmit = async (providerName) => {
    setErrors({});

    const gateway = emailGateways[providerName];

    if (!validateProvider(providerName)) return;

    try {
      setLoadingProvider(providerName);
      showLoader();

      const payload = buildPayload(providerName, gateway);

      let res;

      if (gateway._id) {
        // ✅ UPDATE
        res = await emailAPI.updateEmailProvider(gateway._id, payload);
      } else {
        // ✅ CREATE
        res = await emailAPI.createEmailProvider(payload);
      }

      if (res.code === 200) {
        showSuccess(
          `${providerName.toUpperCase()} ${
            gateway._id ? "updated" : "created"
          } successfully`,
        );
        fetchGatewayDetails();
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoadingProvider(null);
      hideLoader();
    }
  };

  const handleDeleteProvider = async (providerName) => {
    const gateway = emailGateways[providerName];

    // ❌ agar backend me exist hi nahi karta
    if (!gateway?._id) {
      setEmailGateways((prev) => ({
        ...prev,
        [providerName]: {
          ...prev[providerName],
          enabled: false,
        },
      }));
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${providerName.toUpperCase()}?`,
    );

    if (!confirmDelete) return;

    try {
      showLoader();

      const res = await emailAPI.deleteEmailProvider(gateway._id);

      if (res.code === 200) {
        showSuccess(`${providerName.toUpperCase()} deleted`);

        // reset state
        setEmailGateways((prev) => ({
          ...prev,
          [providerName]: {
            ...prev[providerName],
            _id: undefined,
            enabled: false,
            config: {}, // ya default config
          },
        }));
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Delete failed");
    } finally {
      hideLoader();
    }
  };

  const handleMakeDefault = async (providerName) => {
    const gateway = emailGateways[providerName];

    if (!gateway?._id) return;

    try {
      showLoader();

      const res = await emailAPI.defaultEmailProvider(gateway._id);

      if (res.code === 200) {
        showSuccess(`${providerName.toUpperCase()} set as default`);
        fetchGatewayDetails(); // refresh
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Failed to set default");
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Shipping Gateway Settings</h4>
      </div>
      <div className="card-body">
        {errors.general && (
          <div className="col-12 mb-3">
            <div className="alert alert-danger py-2">{errors.general}</div>
          </div>
        )}

        {Object.entries(EMAIL_PROVIDERS).map(([providerName, meta]) => {
          const gateway = emailGateways[providerName];

          return (
            <div className="row" key={providerName}>
              <div className="col-xl-12 d-flex">
                <div className="card flex-fill">
                  <div className="card-body">
                    {/* HEADER */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        <span className="system-app-icon">
                          <img src={meta.icon} alt={meta.label} />
                        </span>

                        <div className="security-title ms-2">
                          <h5 className="fs-16 fw-medium">{meta.label}</h5>
                        </div>
                      </div>

                      <div className="status-toggle modal-status d-flex align-items-center gap-2">
                        {gateway?.isDefault && (
                          <span className="badge bg-success">Default</span>
                        )}

                        <input
                          type="checkbox"
                          id={`${providerName}-toggle`}
                          className="check"
                          checked={!!gateway?.enabled}
                          onChange={() => handleToggle(providerName)}
                        />
                        <label
                          htmlFor={`${providerName}-toggle`}
                          className="checktoggle"
                        ></label>

                        {!gateway?.isDefault && gateway?._id && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleMakeDefault(providerName)}
                          >
                            Make Default
                          </button>
                        )}

                        {!gateway?.isDefault && gateway?._id && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteProvider(providerName)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="fs-12 mb-1">{meta?.description}</p>

                    {gateway?.enabled && (
                      <div className="row shipment">
                        <div className="col-md-12 mb-2">
                          <div className="alert alert-light border">
                            <ol className="ps-3">
                              {meta.setupSteps.map((step, index) => (
                                <li key={index}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        </div>

                        {meta.fields.map((field) => (
                          <div className="col-md-6 mb-2" key={field.name}>
                            <label className="form-label">
                              {field.label}
                              {field.required && (
                                <span className="text-danger">*</span>
                              )}
                            </label>

                            <input
                              type={field.type || "text"}
                              className="form-control"
                              placeholder={field.placeholder}
                              value={gateway.config[field.name] || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  providerName,
                                  field.name,
                                  e.target.value,
                                )
                              }
                            />

                            {/* ERROR */}
                            {errors[`${providerName}_${field.name}`] && (
                              <span className="errors text-danger">
                                {errors[`${providerName}_${field.name}`]}
                              </span>
                            )}
                            <small className="text-muted fs-sm">
                              {field.helperText}
                            </small>
                          </div>
                        ))}

                        <div className="mt-3 d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleProviderSubmit(providerName)}
                          >
                            {loadingProvider === providerName
                              ? "Saving..."
                              : gateway._id
                                ? "Update"
                                : "Save"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default EmailSettings;
